import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { Store } from '@ngxs/store';
import { Activity } from 'app/models/dto/activity.model';
import { ActivitiesNextPage, GetLatestActivities } from 'app/store/activity/activity.actions';
import { ActivityState } from 'app/store/activity/activity.state';
import { startCase } from 'lodash-es';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { Notification } from '../notifications-history.model';
import { activityDisplayAttrsMap } from '../notificationList';
import { LenderUser } from 'app/models/dto/lender-user.model';
import { LenderUserState } from 'app/store/lender-user/lender-user/lender-user.state';

@Component({
  selector: 'app-notifications-history-smart',
  templateUrl: './notifications-history-smart.component.html',
  styleUrls: ['./notifications-history-smart.component.scss'],
  standalone: false
})
export class NotificationsHistorySmartComponent implements OnInit, OnDestroy {
  @Output() clickOutside = new EventEmitter<void>();
  private store = inject(Store);
  private elementRef = inject(ElementRef);
  latestActivities$: Observable<Activity[]> = this.store.select(ActivityState.latestActivities);
  latestActivitiesViewed$: Observable<number> = this.store.select(ActivityState.latestActivitiesViewed);
  loading$: Observable<boolean> = this.store.select(ActivityState.loading);
  nextPageLoading$: Observable<boolean> = this.store.select(ActivityState.nextPageLoading);
  noMoreNotifications$: Observable<boolean> = this.store.select(ActivityState.noMoreNotifications);
  lenderUser$: Observable<LenderUser | null> = this.store.select(LenderUserState.lenderUser);
  notifications$: Observable<Notification[]>;
  destroyed$ = new Subject<void>();
  currentUser: LenderUser | null;
  notificationTypesStorage: [];

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const clickedInside = this.elementRef.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.clickOutside.emit();
    }
  }


  ngOnInit(): void {
    this.lenderUser$.pipe(takeUntil(this.destroyed$)).subscribe(currentUser => {
      this.currentUser = currentUser;
      if (this.currentUser) {
        this.notificationTypesStorage = this.getLocalStorageNotificationTypes();
        const criteria = {
          types: [] = this.notificationTypesStorage,
          start: undefined,
          end: undefined,
          reset: true
        };
        this.store.dispatch(new GetLatestActivities(criteria));
      }
    });
    this.subscribeToNotifications();
  }

  subscribeToNotifications(): void {
    this.notifications$ = this.latestActivities$.pipe(
      map(activities => activities.map(activity => this.activityToNotification(activity)))
    );
  }

  activityToNotification(activity: Activity): Notification {
    const overrides = activityDisplayAttrsMap[activity.type] || {};
    const defaults = {
      icon: 'notifications',
      title: startCase(activity.type),
      businessName: activity.borrowerName,
      timestamp: activity.createdTimestamp,
      type: activity.type,
      subtype: activity.subType,
      borrowerId: activity.borrowerId,
    };

    const notification = Object.assign({}, defaults, overrides);

    if (typeof notification.title === 'function') {
      notification.title = notification.title(activity);
    }

    return notification;
  }

  handleNotificationFilters(criteria) {
    if (!criteria.types.length) {
      this.clearLocalStorageNotificationTypes();
    }
    this.setLocalStorageNotificationTypes(criteria.types);
    this.store.dispatch(new GetLatestActivities(criteria));
  }

  handleLoadMore(criteria) {
    this.store.dispatch(new ActivitiesNextPage(criteria));
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
  }

  private getLocalStorageNotificationTypes(): [] {
    return JSON.parse(localStorage.getItem('savedNotificationTypes') ?? '[]');
  }

  private setLocalStorageNotificationTypes(types: []): void {
    localStorage.setItem('savedNotificationTypes', JSON.stringify(types));
    this.notificationTypesStorage = this.getLocalStorageNotificationTypes();
  }

  private clearLocalStorageNotificationTypes(): void {
    localStorage.removeItem('savedNotificationTypes');
    this.notificationTypesStorage = this.getLocalStorageNotificationTypes();
  }
}
